import { useContext } from "react";
import { Button } from "@blueprintjs/core";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import {
  faCloudArrowUp,
  faCodeBranch as faCodeBranchLight,
  faFolderArrowUp,
  faLink as faLinkLight,
  faPlusCircle as faPlusCircleLight,
  faShareFromSquare,
  faTrashAlt,
} from "@fortawesome/pro-light-svg-icons";
import {
  faCodeCommit,
  faEllipsisVertical,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Menu } from "@mantine/core";

import { WorkbenchContext } from "@pages/editor/providers";
import { SnippetAliasAddress } from "@pages/editor/stores";
import { deleteUserRule } from "@pages/registry/api";

import { Permission } from "src/types/Permissions";

const CustomMenuItem: React.FC<{
  text: string;
  icon: IconProp;
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
}> = ({ text, icon, onClick, disabled = false }) => (
  <Menu.Item
    icon={<FontAwesomeIcon fixedWidth icon={icon} />}
    onClick={onClick}
    disabled={disabled}
  >
    {text}
  </Menu.Item>
);

const EditorEllipsisMenu: React.FC<{ disabled: boolean }> = ({ disabled }) => {
  const { workbench } = useContext(WorkbenchContext);
  const isUserLoggedIn = workbench.user !== undefined;
  const canCreateNewRule =
    workbench.permissions.includes(Permission.editor_create) || !isUserLoggedIn;
  const canForkRule = canCreateNewRule && workbench.bundle;
  const clickForkRule = () => {
    workbench.setShowUnsavedChangesWarning(false);
    workbench.forkRule();
    return;
  };
  const canDeleteRule =
    isUserLoggedIn &&
    workbench.address &&
    workbench.address instanceof SnippetAliasAddress;
  const clickDeleteRule = () => {
    if (!canDeleteRule) return;
    deleteUserRule(workbench.address.fullName)
      .catch(workbench.handleApiError)
      .then(() => workbench.newBundle())
      .then(() => workbench.reloadAllData());
  };
  const clickShareRule = async (isPublic: boolean) => {
    if (
      workbench.hasUnsavedChanges &&
      workbench.org &&
      workbench.bundle &&
      workbench.doesUserHaveEditPermission
    ) {
      await workbench.saveBundle();
    }
    workbench.ui.openRuleShareDialog(isPublic);
  };

  const urlParams = new URLSearchParams(workbench.history.location.search);
  const isDevelop = urlParams.get("develop") === "true";

  return (
    <Menu withArrow withinPortal position="bottom-end">
      {/* // TODO disabled? */}
      <Menu.Dropdown>
        <CustomMenuItem
          icon={faPlusCircleLight}
          onClick={() => workbench.newBundle()}
          text="New"
          disabled={!canCreateNewRule}
        />
        <CustomMenuItem
          icon={faCodeBranchLight}
          onClick={clickForkRule}
          text="Fork"
          disabled={!canForkRule}
        />
        <Menu.Divider />
        <CustomMenuItem
          icon={faLinkLight}
          onClick={() =>
            navigator.clipboard.writeText(window.location.toString())
          }
          text="Copy link"
        />
        <CustomMenuItem
          icon={faShareFromSquare}
          onClick={() => clickShareRule(false)}
          text="Share..."
        />
        <CustomMenuItem
          icon={faFolderArrowUp}
          onClick={() => workbench.ui.openImportRulesDialog()}
          disabled={!isUserLoggedIn}
          text="Import..."
        />
        <CustomMenuItem
          icon={faCloudArrowUp}
          onClick={() => clickShareRule(true)}
          disabled={!isUserLoggedIn}
          text="Publish..."
        />
        <Menu.Divider />
        <CustomMenuItem
          icon={faTrashAlt}
          onClick={clickDeleteRule}
          text="Delete"
          disabled={!canDeleteRule}
        />
        <Menu.Divider />
        <CustomMenuItem
          icon={faCodeCommit}
          onClick={() => {}}
          text={`Semgrep v${
            isDevelop
              ? "Dev"
              : workbench?.org?.editor_semgrep_version || "Latest"
          }`}
          disabled={true}
        />
      </Menu.Dropdown>
      <Menu.Target>
        <div>
          <Button disabled={disabled} outlined style={{ flexShrink: 0 }}>
            <FontAwesomeIcon icon={faEllipsisVertical} />
          </Button>
        </div>
      </Menu.Target>
    </Menu>
  );
};
export default EditorEllipsisMenu;
